import moment from "moment";

import AddressServices from "../../../../../../services/AddressServices";
import ConstantsServices from "../../../../../../services/ConstantsServices";
import HocelotServices from "../../../../../../services/HocelotServices";
import { RELACIONES_REPRESENTANTE } from "../../../../../../constants/salud-pymes-constants";

import {
  getAge,
  isValidDocumentID,
  isValidMobile,
  isValidSelfEmployedDocType,
} from "../../../../../../utils/validation";
import { isValidDate } from "../../../../../../utils/time";
import { Country, Coverage } from "../salud-pymes-insured-types";

export const getInsuredAge = (birthDate: string): number => {
  const [day, month, year] = birthDate.split("/").map(Number);
  const birthDateFormatted = new Date(year, month - 1, day);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  return getAge(currentDate, birthDateFormatted);
};

export const getValidCapitalValues = (coverage: Coverage): string[] => {
  return coverage && coverage.capitales ? coverage.capitales.map((capital) => capital.importe) : [];
};

const getIsFutureDate = (date: string): boolean => {
  const momentDate = moment(date, "DD/MM/YYYY");
  const today = moment().startOf("day");
  return momentDate.isAfter(today);
};

export const validateRequiredCoveragesAge = (
  birthDate: string,
  coverages: Coverage[],
  rowNumber: number,
): string[] => {
  const errors = [];
  if (!isValidDate(birthDate)) {
    errors.push(`Fecha de nacimiento inválida: ${birthDate} (fila ${rowNumber})`);
  }
  const covReqMaxAge = coverages.filter((cov) => {
    if (cov.edadMax !== "" && (cov.required || cov.groupRequired)) {
      return true;
    }
    return false;
  });
  const age = getInsuredAge(birthDate) || 0;
  const isHighAge = covReqMaxAge.some((cov) => age > Number(cov.edadMax));
  if (isHighAge) {
    errors.push(
      `El asegurado con fecha de nacimiento ${birthDate} excede de la edad máxima permitida para alguna de las coberturas requeridas (fila ${rowNumber}).`,
    );
  }
  return errors;
};

export const validateCoverageAgeRange = (
  birthDate: string,
  coverage: Coverage,
  rowNumber: number,
): string[] => {
  const errors = [];
  const insuredAge = getInsuredAge(birthDate) || 0;
  const { edadMin, edadMax, description, code } = coverage;
  const minAge = typeof edadMin === "number" ? edadMin : parseInt(edadMin);
  const maxAge = typeof edadMax === "number" ? edadMax : parseInt(edadMax);

  if (maxAge && insuredAge > maxAge) {
    errors.push(
      `El asegurado con fecha de nacimiento ${birthDate} excede de la edad máxima permitida para la cobertura "${code}-${description}" (fila ${rowNumber})`,
    );
  }
  if (minAge && insuredAge < minAge) {
    errors.push(
      `El asegurado con fecha de nacimiento ${birthDate} excede de la edad mínima permitida para la cobertura "${code}-${description}" (fila ${rowNumber})`,
    );
  }
  return errors;
};

export const validateDocument = ({
  docType,
  docNumber,
  nationality,
  birthDate,
  passportDate,
  rowNumber,
}: {
  docType: string;
  docNumber: string;
  nationality: string;
  birthDate: string;
  passportDate: string;
  rowNumber: number;
}): string[] => {
  const errors = [];
  if (!docType) {
    errors.push(`El tipo de documento es obligatorio (fila ${rowNumber})`);
    return errors;
  }
  const isValidDocType = isValidSelfEmployedDocType({
    docType,
    nationality,
    isInsured: true,
    hasUnderAgeOption: true,
  });
  if (!isValidDocType) {
    errors.push(
      `El tipo de documento no es válido para los datos del asegurado (fila ${rowNumber})`,
    );
    return errors;
  }
  if (docType === "MENOR") {
    if (getInsuredAge(birthDate) >= 14) {
      errors.push(`El tipo de documento no es válido para mayores de 14 años (fila ${rowNumber})`);
    }
    return errors;
  }
  if (!docNumber) {
    errors.push(
      `El número de documento es obligatorio si el tipo de documento no es "MENOR" (fila ${rowNumber})`,
    );
    return errors;
  }
  if (docType === "PASP" && !passportDate) {
    errors.push(`La fecha de caducidad del pasaporte es obligatoria (fila ${rowNumber})`);
  } else if (docType === "PASP" && passportDate) {
    const isPassportDateValid = isValidDate(passportDate);
    const isFuturePassportDate = isPassportDateValid ? getIsFutureDate(passportDate) : false;
    if (!isPassportDateValid || !isFuturePassportDate) {
      errors.push(`Error en la fecha de caducidad del pasaporte (fila ${rowNumber})`);
    }
    return errors;
  }
  const isValidID = isValidDocumentID(docNumber, docType);
  if (!isValidID) {
    errors.push(`Error en el número de documento (fila ${rowNumber})`);
    return errors;
  }
  return errors;
};

export const validatePostalCode = async (postalCode: string): Promise<string[]> => {
  const errors = [];
  if (postalCode.length !== 5) {
    errors.push(`Código postal inválido: ${postalCode}`);
    return errors;
  }
  const resPostalCodes = await AddressServices.getPostalCodes({
    loader: false,
    setLoading: () => {},
    params: [`input=${postalCode}`, `provinceCode=*`, `cityCode=*`, `street=0`],
  });

  if (resPostalCodes.length === 0) {
    errors.push(`Código postal inválido: ${postalCode}`);
  }

  return errors;
};

export const validateEmail = async (email: string): Promise<string[]> => {
  const errors = [];
  const resEmails = await HocelotServices.verifyEmail({
    data: { email },
    setLoading: () => {},
  });
  if (!resEmails.isVerified) {
    errors.push(`Email inválido: ${email}`);
  }
  return errors;
};

export const validateMobilePhone = (
  prefix: string,
  mobilePhone: string,
  countries: Country[],
  rowNumber: number,
): string[] => {
  const errors: string[] = [];
  const country = countries.filter((item) => item.prefix === prefix);

  if (!country || !country.length) {
    errors.push(`Prefijo telefónico no válido (fila ${rowNumber})`);
    return errors;
  }

  if (prefix === "34") {
    if (!isValidMobile(mobilePhone)) {
      errors.push(`Número de teléfono no válido (fila ${rowNumber})`);
    }
  } else {
    if (mobilePhone.length > 13) {
      errors.push(`Número telefónico no válido (fila ${rowNumber})`);
    }
  }

  return errors;
};

export const getValidatedCountryValues = (
  nationalityLabel: string,
  countries: Country[],
  rowNumber: number,
): { nationalityValue: string; error: string } => {
  const country = countries.filter(
    (item) => item.label.toLocaleLowerCase() === nationalityLabel.toLocaleLowerCase(),
  );

  if (!country || !country.length) {
    return {
      nationalityValue: "",
      error: `Revise el país: ${nationalityLabel} (fila ${rowNumber})`,
    };
  }
  return {
    nationalityValue: country[0].value,
    error: "",
  };
};

export const getValidatedRelationship = (
  relationshipLabel: string,
  relationshipList: { value: string; label: string }[],
  rowNumber: number,
): { relationshipValue: string; error: string } => {
  const relationship = relationshipList.filter(
    (item) => item.label.toUpperCase() === relationshipLabel.toLocaleUpperCase(),
  );
  if (!relationship || !relationship.length) {
    return {
      relationshipValue: "",
      error: `Revise la relación del representante: ${relationshipLabel} (fila ${rowNumber})`,
    };
  }
  return {
    relationshipValue: relationship[0].value,
    error: "",
  };
};

export const getRelationshipList = async (): Promise<{ value: string; label: string }[]> => {
  const response = await ConstantsServices.getRelationShips({
    setLoading: () => {},
    loader: false,
  });
  if (response.ok) {
    return response.data.map((item) => ({ value: item.code, label: item.description }));
  }
  return RELACIONES_REPRESENTANTE;
};
